import { z } from 'zod'
import { timeOfDaySchema } from '../validators/time-of-day.validator.js'
import { instantSchema } from '../validators/temporal.validator.js'
import { zoneSchema } from './zone.dto.js'
import { staticLabelSchema } from './static-label.dto.js'
import { storedFurnitureAreasSchema } from './furniture-area.dto.js'
import { storedLandmarksSchema } from './landmark.dto.js'
import { spaceInventoryEntryDtoSchema } from './space-inventory.dto.js'
import { spaceVersionSummarySchema } from './space-version.dto.js'
import { eventTrackDtoSchema } from './event-track.dto.js'
import { userDtoSchema } from './user.dto.js'
import { browserAssetUrlSchema } from '../helpers/asset-ref.js'
import { focalPointSchema } from '../helpers/focal-point.js'

/**
 * A venue space: a distinct area of a venue with its own background floor plan
 * (`zones` + `labels`), dimensions, and furniture `inventory`. Event plans layer
 * furniture on top, drawn from that inventory.
 *
 * The settings (`width`/`height`/`zones`/`labels`/`inventory`) are those of one
 * effective version, identified by `versionId`; `versions` lists every version
 * of the space so authoring UIs can switch between them. In a venue context the
 * effective version is the one applying now; in an event context it is the
 * version the event's plan is bound to.
 */
/**
 * One photograph of a room, ready for the browser.
 *
 * `url` is signed and short-lived — store nothing from it. The first photograph by
 * `sortOrder` is the one to use wherever a single image is needed.
 */
export const spacePhotoDtoSchema = z.object({
  id: z.string().uuid(),
  url: browserAssetUrlSchema,
  /** Also the photograph's alt text. Null when nobody has described it. */
  caption: z.string().nullable(),
  /** Where the subject is, for cropping to any shape. Null = nobody has chosen. */
  focalPoint: focalPointSchema.nullable(),
  sortOrder: z.number().int(),
})

export type SpacePhotoDto = z.infer<typeof spacePhotoDtoSchema>

export const venueSpaceDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  width: z.number(),
  height: z.number(),
  zones: z.array(zoneSchema),
  labels: z.array(staticLabelSchema),
  /**
   * Where furniture may stand: empty for a room nobody has drawn them on, which
   * then has no rule. See `docs/furniture-areas.md`.
   *
   * Defaults to none so a page loaded from a newer deploy still reads a room
   * from an API that has not caught up: the web app can go live before the API
   * does, and without the default every room would fail to load in between.
   */
  areas: storedFurnitureAreasSchema.default([]),
  /**
   * Named parts of the room people refer to — the stairs, the bar — for an
   * assistant to place things by. Hold nothing. See `docs/landmarks.md`.
   * Defaults to none for the same reason as `areas`.
   */
  landmarks: storedLandmarksSchema.default([]),
  inventory: z.array(spaceInventoryEntryDtoSchema),
  sortOrder: z.number().int(),
  versionId: z.string().uuid(),
  /**
   * When the version on show was last written. Sent back with an edit of its
   * drawing so two people drawing the same room cannot silently overwrite each
   * other: the second save is refused rather than winning.
   */
  versionUpdatedAt: instantSchema,
  versions: z.array(spaceVersionSummarySchema),
  /**
   * Photographs of the room, first one first.
   *
   * Reaches clients: `GET /events/:id` carries the venue's spaces, so a client
   * looking at their own event sees these. That is the point of them — it is how
   * somebody knows what the room looks like — but it means a photograph added here
   * is a photograph shown outside the organization.
   */
  photos: z.array(spacePhotoDtoSchema),
  /** The track this space belongs to (null if unassigned). */
  eventTrackId: z.string().uuid().nullable(),
  /**
   * Prismm's id for this room, when the room came from a Prism import.
   *
   * What a re-import joins on. Names are what people change and what Prismm spells
   * differently from CSE; this does not move.
   */
  prismmHallId: z.number().int().nullable(),
  /**
   * When the venue stopped using the room; null while in use. A retired room
   * keeps its plans but is not offered to new events.
   */
  retiredAt: instantSchema.nullable(),
})

export type VenueSpaceDto = z.infer<typeof venueSpaceDtoSchema>

/** A venue with its spaces. The API returns this; the web parses it at the boundary. */
export const venueDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  address: z.string().nullable(),
  /** The venue's cover photograph, signed for the browser; null if none yet. */
  coverImageUrl: browserAssetUrlSchema.nullable(),
  /** Where the subject of the cover is. Null = nobody has chosen one. */
  coverFocalPoint: focalPointSchema.nullable(),
  supportsConcurrentEvents: z.boolean(),
  /**
   * The venue's own clock, as an IANA name — null until somebody sets it.
   *
   * Event times are wall clock and are never converted, so this is not for
   * display: it is what turns "23:00 on the 14th" into a real instant, which is
   * what deciding whether an event has finished needs. See
   * `docs/event-scheduling.md`.
   *
   * **A plain string, deliberately, where the input schema uses `timezoneSchema`.**
   * The 34-name allowlist is a rule about what may be *written*; applying it to
   * what is read turns any stored value outside the list into a venue page that
   * will not load and a Prism import that dies parsing its own response. There
   * are already such values — the list narrowed from all 418 IANA names after
   * the column shipped — and a future ICU could retire a name we pinned.
   * `timezone.validator.ts` says why: "Validate on write, never on read …
   * re-checking old rows would fail them for something nobody did."
   */
  timezone: z.string().nullable(),
  /**
   * The venue's usual evening, filled in when an inquiry is given a date.
   *
   * `timeOfDaySchema` checks the *format*, which is not the thing that had to be
   * loosened — the 34-name allowlist on `timezone` was. A format check is a
   * property of the value, not a policy that can be narrowed later, so it
   * cannot retroactively fail a row nobody touched.
   */
  defaultStartsAt: timeOfDaySchema,
  defaultEndsAt: timeOfDaySchema,
  /** Prismm's id for this venue, when it came from a Prism import. */
  prismmVenueRef: z.number().int().nullable(),
  tracks: z.array(eventTrackDtoSchema),
  spaces: z.array(venueSpaceDtoSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
})

export type VenueDto = z.infer<typeof venueDtoSchema>

/**
 * The time zones this API will accept, for the venue settings form's dropdown.
 *
 * Served rather than computed in the browser: the two runtimes read the same ICU
 * data but are upgraded at different times, so a list built client-side could
 * offer a name the server then rejects. Asking the server what it accepts makes
 * that impossible.
 */
export const timezoneListDtoSchema = z.object({
  timezones: z.array(z.string()),
})

export type TimezoneListDto = z.infer<typeof timezoneListDtoSchema>

export const venueListDtoSchema = z.object({
  venues: z.array(venueDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VenueListDto = z.infer<typeof venueListDtoSchema>

/** The venue-admin users assigned to manage a venue. */
export const venueManagerListDtoSchema = z.object({
  managers: z.array(userDtoSchema),
})

export type VenueManagerListDto = z.infer<typeof venueManagerListDtoSchema>
